import { fetchHoj } from './client';
import type { Venue } from './types';

interface VenueList {
  data: Venue[];
  meta?: { nextCursor?: string | null };
}

const CACHE_KEY = 'wp:venuesCache';

let inflight: Promise<Map<string, Venue>> | null = null;

async function fetchAllVenues(): Promise<Venue[]> {
  const out: Venue[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchHoj<VenueList>('/v1/venues', {
      query: { limit: 100, cursor },
    });
    out.push(...page.data);
    cursor = page.meta?.nextCursor ?? undefined;
  } while (cursor);
  return out;
}

/**
 * Returns a map from venueId to Venue, fully paginated and cached for the
 * lifetime of the tab. The HofJ public Product DTO does not include cover
 * images, so the listing UI relies on the venue's cover as the product hero.
 */
export async function getVenueMap(): Promise<Map<string, Venue>> {
  if (inflight) return inflight;

  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) {
      const list = JSON.parse(raw) as Venue[];
      const map = new Map<string, Venue>();
      for (const v of list) map.set(v.id, v);
      return map;
    }
  } catch {
    // ignore
  }

  inflight = fetchAllVenues()
    .then((list) => {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(list));
      } catch {
        // ignore
      }
      const map = new Map<string, Venue>();
      for (const v of list) map.set(v.id, v);
      return map;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

export async function getVenue(id: string): Promise<Venue | undefined> {
  const map = await getVenueMap();
  return map.get(id);
}
